import { Link } from 'react-router-dom';

const LAST_UPDATED = 'February 27, 2026';
const COMPANY = 'Outlaw Oasis';
const SITE_URL = 'https://www.outlawoasis.com';
const CONTACT_EMAIL = 'legal@outlawoasis.com';

export default function Terms() {
  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="font-western text-4xl sm:text-5xl text-gold mb-4">Terms of Service</h1>
          <p className="text-text-muted text-sm">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="prose-legal space-y-10 text-text-secondary text-sm leading-relaxed">

          {/* Introduction */}
          <section>
            <p>
              Welcome to {COMPANY} ("{COMPANY}", "we", "us", or "our"), accessible at{' '}
              <span className="text-gold">{SITE_URL}</span>. By accessing or using our platform, you
              agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms,
              you must not use our platform.
            </p>
            <p className="mt-4">
              These Terms constitute a legally binding agreement between you and {COMPANY}. Please read
              them carefully.
            </p>
          </section>

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              1. Eligibility
            </h2>
            <p>
              You must be at least 18 years of age (or the age of majority in your jurisdiction,
              whichever is greater) to use {COMPANY}. By using our platform, you represent and warrant
              that you meet this requirement. We reserve the right to request age verification at any
              time and to suspend or terminate accounts that do not meet this requirement.
            </p>
            <p className="mt-3">
              By creating an account, you agree that all information you provide is accurate, complete,
              and current. You are solely responsible for maintaining the confidentiality of your
              account credentials and for all activity that occurs under your account.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              2. Description of Services
            </h2>
            <p>
              {COMPANY} is a peer-to-peer digital marketplace that facilitates the buying and selling
              of digital goods, including but not limited to:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-2">
              <li>Game accounts and in-game assets</li>
              <li>Game enhancement software, cheats, and modifications ("cheats")</li>
              <li>Digital services and software subscriptions</li>
              <li>Other digital goods as listed on the platform</li>
            </ul>
            <p className="mt-3">
              {COMPANY} acts solely as an intermediary platform. We do not independently create, develop,
              or guarantee any product listed by third-party sellers. All transactions are between buyers
              and sellers directly.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              3. Third-Party Intellectual Property &amp; Trademarks Disclaimer
            </h2>
            <p>
              {COMPANY} is an independent marketplace and is <strong className="text-cream">not affiliated
              with, endorsed by, sponsored by, or officially connected</strong> to any game developer,
              game publisher, software company, or intellectual property holder whose products, names,
              logos, or trademarks may appear on our platform.
            </p>
            <p className="mt-3">
              All game titles, logos, brand names, trademarks, service marks, and trade names referenced
              on our platform (including but not limited to those belonging to game developers and
              publishers) are the property of their respective owners. Any such references are made solely
              for the purpose of identifying the type of product being sold (i.e., nominative fair use)
              and do not imply any affiliation, authorization, or endorsement.
            </p>
            <p className="mt-3">
              Sellers are responsible for ensuring they have the right to sell any product listed on our
              platform. If you believe your intellectual property rights have been infringed, please
              contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold hover:underline">
                {CONTACT_EMAIL}
              </a>{' '}
              with a detailed description of the alleged infringement and we will investigate promptly.
            </p>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              4. Assumption of Risk — Game Accounts &amp; Cheats
            </h2>
            <p className="font-medium text-cream">
              BY PURCHASING ANY PRODUCT ON {COMPANY.toUpperCase()}, YOU EXPRESSLY ACKNOWLEDGE AND ACCEPT
              THE FOLLOWING RISKS:
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="text-cream font-medium mb-1">4.1 Game Accounts</h3>
                <p>
                  The purchase, sale, or transfer of game accounts may violate the Terms of Service
                  or End User License Agreement ("EULA") of the respective game or platform. Game
                  developers and publishers may at any time, with or without notice, suspend, ban,
                  or permanently terminate any account that they determine to have been transferred,
                  sold, or purchased in violation of their policies. {COMPANY} is not responsible for
                  any such action taken by a game developer or publisher, and no refund will be
                  issued on account of such enforcement actions.
                </p>
              </div>
              <div>
                <h3 className="text-cream font-medium mb-1">4.2 Cheats &amp; Game Enhancement Software</h3>
                <p>
                  The use of cheats, hacks, aimbots, ESP software, or other game enhancement tools
                  may violate the Terms of Service or EULA of the respective game or anti-cheat
                  system. Use of such software may result in temporary or permanent bans from the
                  game, loss of in-game progress, or legal action by the game developer. You assume
                  full and sole responsibility for any consequences arising from the use of cheats
                  purchased through our platform. {COMPANY} does not guarantee the functionality,
                  undetectability, safety, or continued availability of any cheat software.
                </p>
              </div>
              <div>
                <h3 className="text-cream font-medium mb-1">4.3 No Guarantees on Third-Party Actions</h3>
                <p>
                  {COMPANY} has no control over the policies, decisions, or enforcement actions of any
                  game developer, publisher, or platform operator. We cannot and do not guarantee that
                  any product will remain functional, accessible, or undetected after purchase. Anti-cheat
                  updates, game patches, and policy changes may render purchased products ineffective.
                </p>
              </div>
            </div>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              5. User Conduct &amp; Prohibited Activities
            </h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
              <li>Use our platform for any unlawful purpose or in violation of applicable law</li>
              <li>List, sell, or purchase stolen accounts or goods obtained through unauthorized means</li>
              <li>Engage in fraud, chargebacks, or payment disputes in bad faith</li>
              <li>Attempt to hack, disrupt, or interfere with our platform or any user's account</li>
              <li>Use automated tools, bots, or scrapers without prior written permission</li>
              <li>Impersonate another user, seller, or {COMPANY} staff member</li>
              <li>Post misleading, false, or deceptive product listings</li>
              <li>Harass, threaten, or abuse other users or our staff</li>
              <li>
                Attempt to conduct transactions outside of our platform to circumvent fees or protections
              </li>
              <li>Sell products that violate the rights of any third party</li>
            </ul>
            <p className="mt-3">
              Violation of these rules may result in immediate account suspension or termination, forfeiture
              of funds, and/or referral to appropriate law enforcement agencies.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              6. Seller Responsibilities
            </h2>
            <p>
              Sellers on {COMPANY} are independent third parties and are solely responsible for the
              accuracy of their listings, the quality and legality of their products, and prompt
              delivery of goods after purchase. By listing a product, sellers represent and warrant
              that:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
              <li>They have the right to sell the listed product</li>
              <li>All product descriptions are accurate and not misleading</li>
              <li>The product will be delivered as described within the stated timeframe</li>
              <li>They will comply with all applicable laws and {COMPANY} policies</li>
            </ul>
            <p className="mt-3">
              {COMPANY} reserves the right to remove any listing, suspend any seller account, and
              withhold or refund payments at its sole discretion if a seller is found to be in breach
              of these Terms or our seller policies.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              7. Payments &amp; Fees
            </h2>
            <p>
              All transactions on {COMPANY} are processed through our third-party payment processors.
              By making a purchase, you authorize the applicable charge to your selected payment method.
              Prices are displayed inclusive or exclusive of applicable taxes as required by law.
            </p>
            <p className="mt-3">
              {COMPANY} charges a platform fee on each transaction, the amount of which is disclosed
              at the time of listing or purchase. This fee is non-refundable.
            </p>
            <p className="mt-3">
              Fraudulent chargebacks or unauthorized payment disputes may result in immediate account
              termination, recovery of funds owed, and referral to collection agencies or legal action.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              8. Refunds &amp; Replacements
            </h2>
            <p>
              All sales of digital products are final. {COMPANY} does not offer refunds. In limited
              circumstances, and entirely at our sole discretion, we may offer a replacement product
              of equivalent or similar value. Please refer to our{' '}
              <Link to="/refunds" className="text-gold hover:underline">
                Refund Policy
              </Link>{' '}
              for full details.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              9. Intellectual Property of {COMPANY}
            </h2>
            <p>
              All content, design, code, branding, and materials that constitute the {COMPANY} platform
              itself — including but not limited to our logo, name, layout, and original written content
              — are the exclusive property of {COMPANY} and are protected by applicable intellectual
              property laws. You may not copy, reproduce, distribute, or create derivative works from
              our proprietary content without prior written permission.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              10. Disclaimer of Warranties
            </h2>
            <p className="uppercase font-medium">
              THE PLATFORM AND ALL PRODUCTS AVAILABLE THROUGH IT ARE PROVIDED ON AN "AS IS" AND
              "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, OR NON-INFRINGEMENT. {COMPANY.toUpperCase()} DOES NOT WARRANT THAT
              THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL
              COMPONENTS.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              11. Limitation of Liability
            </h2>
            <p className="uppercase font-medium">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY.toUpperCase()} AND ITS
              OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
              LIMITED TO LOSS OF PROFITS, LOSS OF DATA, LOSS OF GAME ACCOUNTS OR PROGRESS, OR BUSINESS
              INTERRUPTION, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE PLATFORM OR ANY
              PRODUCT PURCHASED THROUGH IT, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p className="mt-3 uppercase font-medium">
              IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE AMOUNT YOU PAID TO {COMPANY.toUpperCase()}
              IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              12. Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless {COMPANY} and its officers, directors,
              employees, and agents from and against any claims, liabilities, damages, losses, and
              expenses (including reasonable legal fees) arising out of or in any way connected with
              your access to or use of the platform, your violation of these Terms, your violation of
              any third party's rights, or any product you list or purchase through our platform.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              13. Termination
            </h2>
            <p>
              {COMPANY} reserves the right to suspend or permanently terminate your account and access
              to the platform at any time, with or without notice, for any reason, including but not
              limited to a violation of these Terms. Upon termination, all licenses granted to you
              hereunder immediately cease. Sections that by their nature should survive termination
              (including Sections 4, 10, 11, 12, 14, and 15) will survive.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              14. Governing Law &amp; Dispute Resolution
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable law. Any
              dispute arising out of or relating to these Terms or your use of the platform shall first
              be attempted to be resolved through good-faith negotiation. If unresolved, disputes shall
              be submitted to binding arbitration, to the extent permitted by law.
            </p>
            <p className="mt-3">
              You waive any right to participate in class action lawsuits or class-wide arbitration
              against {COMPANY} to the fullest extent permitted by law.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              15. Changes to These Terms
            </h2>
            <p>
              We reserve the right to modify these Terms at any time. When we do, we will update the
              "Last updated" date at the top of this page. Your continued use of the platform after any
              changes constitutes your acceptance of the new Terms. We encourage you to review these
              Terms periodically.
            </p>
          </section>

          {/* 16 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              16. Contact
            </h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="mt-3 p-4 bg-leather rounded-lg border border-gold/20">
              <p className="text-cream font-semibold">{COMPANY}</p>
              <p className="mt-1">
                Email:{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold hover:underline">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p>
                Website:{' '}
                <a href={SITE_URL} className="text-gold hover:underline">
                  {SITE_URL}
                </a>
              </p>
            </div>
          </section>

          {/* Related links */}
          <div className="pt-6 border-t border-gold/10 flex flex-wrap gap-4 text-sm">
            <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>
            <Link to="/refunds" className="text-gold hover:underline">Refund Policy</Link>
            <Link to="/support" className="text-gold hover:underline">Contact Support</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
